import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialogRef } from "@angular/material";
import { Actions } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { FormBuilder, FormGroup, AbstractControl, Validators, FormControl, FormArray } from '@angular/forms';
import * as actions from '../store/actions';
import * as fromStore from '../store/reducers';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import { ModuleConfiguration } from '../business-rule.module.config';
import { ParameterModel } from "../models/ParameterModel";
import { UpdateParametersModel } from "../models/UpdateParametersModel";
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { GetParametersAction } from '../store/actions/GetParametersAction';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-edit-parameter',
  templateUrl: './edit-parameter.component.html',
  styleUrls: ['./edit-parameter.component.scss']
})
export class EditParameterComponent implements OnInit, OnDestroy {
  key: string;
  updateParametersModel: UpdateParametersModel;
  parameters: ParameterModel[];
  destroyed$: Subject<boolean> = new Subject<boolean>();
  parametersForm: FormGroup;
  initialLoaded: boolean = false;
  loading$: Observable<boolean>;
  errors$: Observable<string[]>;
  allowedDataTypes = ['Text', 'Date'];
  formArrayInitMethods = new Map<string, () => void>();
  editPermission: string = 'RuleModule.BusinessRule:Edit';
  dataType: string;
  picker: Date;

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    public dialogRef: MatDialogRef<EditParameterComponent>,
    private formBuilder: AutoSaveFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private actions$: Actions) {    
  }

  ngOnInit() {
    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];
      if (this.key) {
        this.store.dispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, UpdateParametersModel, { key: this.key }));
      }
    });

    this.store.select(fromStore.getParameters)
      .takeUntil(this.destroyed$)
      .subscribe((v: UpdateParametersModel) => {
        if (this.initialLoaded && v.parameters) {
          this.updateParametersModel = v;
          this.parameters = v.parameters;
          if (!this.parametersForm) {
            this.initForms();
          }
          this.changeDetectorRef.markForCheck();
        }
        if (!this.initialLoaded) {
          this.initialLoaded = true;
          this.store.dispatch(new GetParametersAction({ page: 0, pageSize: 200, sort: 'Name', sortDirection: 'acs', searchTerm: '' }));
        }
      });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UpdateParametersModel, ParameterModel)
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelSuccessAction<UpdateParametersModel, any>) => a.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<UpdateParametersModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelSuccessAction<UpdateParametersModel, any>) => a.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelErrorAction<UpdateParametersModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });

    var removeType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, UpdateParametersModel, ParameterModel);
    this.actions$.ofType(removeType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var parametersArray = (this.parametersForm.controls['parameters'] as FormArray);
        var index = parametersArray.controls.indexOf(payload.control);
        parametersArray.removeAt(index);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  save() {

  }

  addFormGroup(form, name) {
    form.controls[name].push(this.formArrayInitMethods.get(name)());
  }

  close() {
    this.dialogRef.close();
  }

  initForms(): void {
    this.parametersForm = this.formBuilder.group({      
      parameters: this.formBuilder.array(this.updateParametersModel.parameters.map(c => this.initParameter()))
    });

    this.formArrayInitMethods.set('parameters', () => this.initParameter());

    this.parametersForm.patchValue(this.updateParametersModel);
    this.parametersForm.markAsPristine();
  }

  initParameter() {
    return this.formBuilder.group({
      key: [''],
      dataType: [''],
      name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
      value: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
    },
    null,
    this.destroyed$,
      (control) => {
        var model = control.value;
        if (control.value.dataType === 'Date') {
          model.value = control.value.value.format('MM/DD/YYYY');
        }
      this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UpdateParametersModel, ParameterModel, { key: this.key, model: model, control: control }));
    },
    this.changeDetectorRef);
  }

  selectType(event, parameter) {
    parameter.dataType = event.value;
  }

  removeParameter(form, parameter: AutoSaveFormGroup, index) {
    parameter.initializeRemove();
    this.store.dispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, UpdateParametersModel, ParameterModel, { key: this.key, model: parameter.value, control: parameter }));
  }
}



// WEBPACK FOOTER //
// ./src/app/businessRule/components/edit-parameter.component.ts